import React from 'react';
import Space from 'antd/lib/space';
import { CheckOutlined, CloseOutlined, FileDoneOutlined } from '@ant-design/icons';
import { antGrey, runwayColors } from './styles/globalStyles';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../helpers/aclHelpers';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';

interface ApprovalButtonsProps {
    releaseId: string;
    isApproved: boolean;
    disableApproveButton: boolean;
    approvalIsLoading: boolean;
    updateApprovalOverrideIsLoading: boolean;
    onClickApprove: () => void;
    updateApprovalOverride?: (form: { requiresManualApproval: boolean }) => void;
    stepLabel: string;
    isWorkflowStep: boolean;
    approveLabel?: string;
}

export const ApprovalButtons: React.FC<ApprovalButtonsProps> = ({
    releaseId,
    isApproved,
    disableApproveButton,
    approvalIsLoading,
    updateApprovalOverrideIsLoading,
    onClickApprove,
    updateApprovalOverride,
    stepLabel,
    isWorkflowStep,
    approveLabel,
}) => {
    const approvalButtonLabel = isApproved ? `Rescind ${stepLabel} approval` : (approveLabel ?? `Approve ${stepLabel}`);

    return (
        <>
            {isWorkflowStep ?
                <div style={{ display: 'inline-flex' }}>
                    <PermissionedActionButton
                        releaseId={releaseId}
                        actionType={RunwayUserAction.UAApproveScreenshotsMetadata}
                        loading={approvalIsLoading || updateApprovalOverrideIsLoading}
                        disabled={disableApproveButton || updateApprovalOverrideIsLoading || approvalIsLoading}
                        onClick={onClickApprove}
                        type={isApproved ? 'default' : 'primary'}
                        icon={
                            isApproved ? <CloseOutlined style={{ color: runwayColors.statusRed }} /> : <CheckOutlined />
                        }
                        menu={{
                            items: [
                                {
                                    key: 'autoApprove',
                                    label: (
                                        <Space>
                                            Auto-approve when ready
                                            <TooltipInfoIcon
                                                tooltipContent={
                                                    isApproved ?
                                                        'Step has already been manually approved.'
                                                    :   'This will only affect the current release.'
                                                }
                                            />
                                        </Space>
                                    ),
                                    icon: <FileDoneOutlined />,
                                    disabled: isApproved,
                                },
                            ],
                            onClick: () =>
                                updateApprovalOverride?.({
                                    requiresManualApproval: false,
                                }),
                        }}
                        shouldUseCustomButtonsRender
                        buttonsRender={([leftButton, rightButton]) => [
                            React.cloneElement(leftButton as React.ReactElement, {
                                icon:
                                    isApproved ?
                                        <CloseOutlined style={{ color: runwayColors.statusRed }} />
                                    :   <CheckOutlined />,
                            }),
                            rightButton,
                        ]}
                    >
                        {approvalButtonLabel}
                    </PermissionedActionButton>
                </div>
            :   <PermissionedActionButton
                    releaseId={releaseId}
                    actionType={RunwayUserAction.UAApproveScreenshotsMetadata}
                    loading={approvalIsLoading}
                    disabled={disableApproveButton}
                    onClick={onClickApprove}
                    type={isApproved ? 'default' : 'primary'}
                    icon={isApproved ? <CloseOutlined style={{ color: runwayColors.statusRed }} /> : <CheckOutlined />}
                >
                    {approvalButtonLabel}
                </PermissionedActionButton>
            }
        </>
    );
};

interface AutoApproveEnabledButtonProps {
    releaseId: string;
    updateApprovalOverrideIsLoading: boolean;
    updateApprovalOverride?: (form: { requiresManualApproval: boolean }) => void;
}

export const AutoApproveEnabledButton: React.FC<AutoApproveEnabledButtonProps> = ({
    releaseId,
    updateApprovalOverrideIsLoading,
    updateApprovalOverride,
}) => {
    return (
        <div style={{ display: 'inline-flex' }}>
            <PermissionedActionButton
                releaseId={releaseId}
                actionType={RunwayUserAction.UAApproveScreenshotsMetadata}
                loading={updateApprovalOverrideIsLoading}
                type="default"
                icon={<FileDoneOutlined />}
                menu={{
                    items: [
                        {
                            key: 'requireManualApproval',
                            label: (
                                <Space>
                                    Require manual approval
                                    <TooltipInfoIcon tooltipContent="This will only affect the current release." />
                                </Space>
                            ),
                        },
                    ],
                    onClick: () =>
                        updateApprovalOverride?.({
                            requiresManualApproval: true,
                        }),
                }}
                shouldUseCustomButtonsRender
                buttonsRender={([leftButton, rightButton]) => [
                    React.cloneElement(leftButton as React.ReactElement, {
                        icon: <FileDoneOutlined style={{ color: antGrey.grey6 }} />,
                        style: { cursor: 'default', color: antGrey.grey6 },
                        onClick: (e: React.MouseEvent) => e.preventDefault(),
                    }),
                    React.cloneElement(rightButton as React.ReactElement, {
                        disabled: updateApprovalOverrideIsLoading,
                    }),
                ]}
            >
                Auto-approve enabled
            </PermissionedActionButton>
        </div>
    );
};
